import { useParams, useOutletContext } from "react-router-dom";
import { useState, useEffect } from "react";
import axiosClient from "../../axios";

export default function LessonPage({ termId }) {
  const { slug1, slug2, slug3 } = useParams();
  const [videos, setVideos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);

        const response = await axiosClient.get("/api/getLessonsByTermId", {
          params: {
            term_id: termId,
          },
        });

        setVideos(response.data);

        setError(null);
      } catch (error) {
        console.error("Error fetching videos:", error);

        setError(error?.response?.data?.message || "Failed to load lessons.");
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, [slug3, termId]);

  if (loading) {
    return (
      <div className="container py-4">
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading lessons...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div
          className="alert alert-danger d-flex align-items-center"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <div>{error}</div>
        </div>
      </div>
    );
  }

  if (!Array.isArray(videos) || videos.length === 0) {
    return (
      <div className="container py-4">
        <div className="card shadow-sm border-0">
          <div className="card-body text-center py-5">
            <i className="bi bi-camera-video-off display-4 text-muted mb-3"></i>
            <h5 className="text-muted">No Lessons Available</h5>
            <p className="text-muted">
              Lessons will be added to this course soon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="h5 mb-0">Course Lessons</h2>
            <span className="badge bg-primary">
              {videos.length} lesson{videos.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="accordion" id="lessonAccordion">
            {videos.map((video, index) => (
              <div className="accordion-item border-0" key={index}>
                <h2 className="accordion-header" id={`heading-${index}`}>
                  <button
                    className={`accordion-button ${
                      index !== activeIndex ? "collapsed" : ""
                    } py-3`}
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse-${index}`}
                    aria-expanded={index === activeIndex}
                    aria-controls={`collapse-${index}`}
                    onClick={() =>
                      setActiveIndex(index === activeIndex ? null : index)
                    }
                  >
                    <div className="d-flex align-items-center w-100">
                      <div
                        className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "36px",
                          height: "36px",
                          minWidth: "36px",
                        }}
                      >
                        <i className="bi bi-play-fill text-white"></i>
                      </div>
                      <div className="flex-grow-1 text-start">
                        <h6 className="mb-0 fw-semibold">
                          {video.postCont ||
                            video.title ||
                            `Lesson ${index + 1}`}
                        </h6>
                        <small className="text-muted">
                          {index === activeIndex
                            ? "Currently playing"
                            : "Click to view"}
                        </small>
                      </div>
                    </div>
                  </button>
                </h2>
                <div
                  id={`collapse-${index}`}
                  className={`accordion-collapse collapse ${
                    index === activeIndex ? "show" : ""
                  }`}
                  aria-labelledby={`heading-${index}`}
                  data-bs-parent="#lessonAccordion"
                >
                  <div className="accordion-body p-0">
                    <div className="ratio ratio-16x9">
                      <iframe
                        src={video.vimeo_uri}
                        frameBorder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        title={
                          video.postCont || video.title || `Video ${index + 1}`
                        }
                        className="rounded-bottom"
                      ></iframe>
                    </div>

                    {/* Video Metadata */}
                    {(video.postCont || video.title) && (
                      <div className="p-3 border-top">
                        <h6 className="fw-semibold mb-2">About this lesson:</h6>
                        <p className="text-muted mb-0 small">
                          {video.postCont || video.title}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Progress Footer */}
        <div className="card-footer bg-white py-3">
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              {activeIndex !== null
                ? `Watching lesson ${activeIndex + 1} of ${videos.length}`
                : "Select a lesson to begin"}
            </small>
            <small className="text-muted">
              <i className="bi bi-info-circle me-1"></i>
              Click on any lesson to expand and watch
            </small>
          </div>
        </div>
      </div>
    </div>
  );
}
